import type { CabinetRoleOption, CreateInvitationRequest, InvitationView } from "@planerius/contracts/cabinet-auth"
import { systemRole } from "@planerius/contracts/cabinet-roles"

import { countOf } from "../../../lib/plural"
import { normalizePhone } from "../../master/phone"

/**
 * Окно «Пригласить в кабинет» (Б9, ТЗ 04-настройки-и-подключение) как данные.
 *
 * Макета у окна нет (4-Б9 в «остатке П1»), поэтому всё, что здесь сказано человеку, взято
 * из ТЗ Б9 и из соседних экранов того же голоса: роль — одной фразой, пустой список
 * филиалов — словами («все филиалы, включая будущие»), ссылка — с честной подписью, что
 * отправлять её салон будет сам (писем и SMS пока нет), как одноразовый ключ 4-В7.
 */

export interface InviteDialogForm {
  /** Почта или телефон одной строкой: ТЗ Б9 — «телефон либо почта». */
  readonly contact: string
  readonly roleId: string
  readonly allBranches: boolean
  readonly branchIds: readonly string[]
  readonly employeeId: string
}

export interface InviteDialogErrors {
  readonly contact: string | null
  readonly role: string | null
  readonly branches: string | null
}

export type ParsedContact =
  | { readonly kind: "email"; readonly email: string }
  | { readonly kind: "phone"; readonly phoneE164: string }
  | { readonly kind: "invalid"; readonly reason: string }

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Почта узнаётся по «@», остальное — телефон. Неполный адрес называется прямо (ТЗ Б9). */
export function parseContact(input: string): ParsedContact {
  const raw = input.trim()
  if (raw.length === 0) return { kind: "invalid", reason: "Укажите почту или телефон — приглашение должно куда-то уйти" }
  if (raw.includes("@")) {
    return EMAIL_SHAPE.test(raw)
      ? { kind: "email", email: raw.toLowerCase() }
      : { kind: "invalid", reason: `Адрес «${raw}» выглядит неполным. Проверьте его — или пригласите по телефону.` }
  }
  const phone = normalizePhone(raw)
  return phone === null
    ? { kind: "invalid", reason: "Номер набран не полностью: нужны все 10 цифр после +7" }
    : { kind: "phone", phoneE164: phone }
}

/** Подпись роли: название и одна фраза — только у системной роли (у своей роли салона фразы нет). */
export function roleOptionLabel(role: CabinetRoleOption): string {
  const summary = role.code === null ? undefined : systemRole(role.code)?.summary
  return summary === undefined ? role.name : `${role.name} — ${summary}`
}

export function inviteDialogErrors(form: InviteDialogForm, roles: readonly CabinetRoleOption[]): InviteDialogErrors {
  const contact = parseContact(form.contact)
  const role = roles.find((item) => item.id === form.roleId)
  return {
    contact: contact.kind === "invalid" ? contact.reason : null,
    role:
      role === undefined ? "Выберите роль"
      : role.grantable ? null
      : (role.notGrantableReason ?? "Эту роль выдаёт владелец салона"),
    branches: form.allBranches || form.branchIds.length > 0 ? null : "Выберите хотя бы один филиал",
  }
}

export function hasDialogErrors(errors: InviteDialogErrors): boolean {
  return errors.contact !== null || errors.role !== null || errors.branches !== null
}

/** Запрос выпуска. Пустой branchIds на сервере означает «все, включая будущие» — только осознанно. */
export function inviteRequest(form: InviteDialogForm): CreateInvitationRequest {
  const contact = parseContact(form.contact)
  if (contact.kind === "invalid") throw new Error("inviteRequest вызван с неразобранным контактом")
  return {
    ...(contact.kind === "email" ? { email: contact.email } : { phoneE164: contact.phoneE164 }),
    roleId: form.roleId,
    branchIds: form.allBranches ? [] : [...form.branchIds],
    ...(form.employeeId === "" ? {} : { employeeId: form.employeeId }),
    expiresInHours: 72,
  }
}

/** Как выглядит «до …» у ссылки. */
function until(instant: string): string {
  const date = new Date(instant)
  if (Number.isNaN(date.getTime())) return "—"
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(date)
}

/**
 * Подпись под ссылкой. Честно: отправляет салон сам, ссылка показывается один раз.
 * Пообещать «письмо ушло» при каналах-заглушках — значит, что человек будет ждать письма,
 * которого нет, а салон — человека, который о приглашении не знает.
 */
export function linkCaption(invitation: InvitationView): string {
  return `Скопируйте ссылку и отправьте её сами — в мессенджере или письмом: сами мы приглашения пока не рассылаем. Ссылка работает до ${until(invitation.expiresAt)} и показывается один раз — потом её можно только выпустить заново.`
}

/** «Висит пятый день» — то, по чему в списке видно, что приглашение забыли (ТЗ Б8). */
export function pendingAge(invitation: InvitationView, now: Date = new Date()): string {
  const sent = new Date(invitation.lastSentAt ?? invitation.createdAt)
  const days = Math.floor((now.getTime() - sent.getTime()) / 86_400_000)
  if (Number.isNaN(days) || days <= 0) return "отправлено сегодня"
  return `висит ${countOf(days, "день", "дня", "дней")}`
}
